/**
 * SoleadLab 2.0 — IV Reporting Store
 */

import { create } from 'zustand';
import type {
  IVSession,
  IVMeasurement,
  IVFinding,
  IVReport,
  CreateSessionInput,
  UpdateSessionInput,
  UpdateMeasurementInput,
  ImportPayload,
  ImportResult,
} from '@/types/iv';
import * as ivApi from '@/services/ivApi';

interface IVState {
  sessions: IVSession[];
  selectedSessionId: string | null;
  measurements: IVMeasurement[];
  findings: IVFinding[];
  reports: IVReport[];
  loading: boolean;
  pendingTab: 'measurements' | null;
  currentSiteId: string | null;
  ivSessionCache: Record<string, IVMeasurement[]>;

  setSiteId: (siteId: string | null) => void;
  loadSessionMeasurements: (sessionId: string) => Promise<void>;
  fetchSessions: () => Promise<void>;
  selectSession: (id: string | null) => Promise<void>;
  setPendingTab: (tab: 'measurements' | null) => void;
  createSession: (input: CreateSessionInput) => Promise<IVSession>;
  updateSession: (id: string, input: UpdateSessionInput) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  importMeasurements: (sessionId: string, payload: ImportPayload) => Promise<ImportResult>;
  fetchMeasurements: (sessionId: string) => Promise<void>;
  toggleMeasurementIncluded: (measurementId: string, included: boolean) => Promise<void>;
  updateMeasurement: (id: string, input: UpdateMeasurementInput) => Promise<void>;
  bulkUpdateMeasurements: (
    sessionId: string,
    ids: string[],
    updates: { label?: string | null; marker2?: string | null; marker3?: string | null; map_label?: string | null },
  ) => Promise<void>;
  bulkAssignMapLabels: (
    sessionId: string,
    assignments: Array<{ id: string; map_label: string | null }>,
  ) => Promise<void>;
  deleteMeasurement: (id: string) => Promise<void>;
  runAnalysis: (sessionId: string) => Promise<void>;
  fetchFindings: (sessionId: string) => Promise<void>;
  generateReport: (sessionId: string) => Promise<void>;
  fetchReports: (sessionId: string) => Promise<void>;
}

export const useIVStore = create<IVState>((set, get) => ({
  sessions: [],
  selectedSessionId: null,
  measurements: [],
  findings: [],
  reports: [],
  loading: false,
  pendingTab: null,
  currentSiteId: null,
  ivSessionCache: {},

  setSiteId: (siteId: string | null) => {
    const prev = get().currentSiteId;
    set({ currentSiteId: siteId });
    if (siteId !== prev) {
      // Clear stale sessions and deselect when switching sites
      set({ sessions: [], selectedSessionId: null, measurements: [], findings: [], reports: [], ivSessionCache: {} });
    }
    get().fetchSessions();
  },

  setPendingTab: (tab) => set({ pendingTab: tab }),

  fetchSessions: async () => {
    const { currentSiteId } = get();
    if (!currentSiteId) {
      set({ sessions: [] });
      return;
    }
    set({ loading: true });
    try {
      const sessions = await ivApi.listSessions(currentSiteId);
      set({ sessions, loading: false });
      // Eager-load EVERY session's measurements (owner ruling 2026-09-24,
      // F-02E-5b). The LayersPanel "IV Results" row renders only once at
      // least one cached measurement is map-assigned (`ivTotalAssigned > 0`),
      // so without this the row is invisible until someone opens the IV
      // ticket and clicks a session — and share viewers have no ticket at
      // all. One small request per session on project open; `ivSessionCache`
      // dedupes, and `loadSessionMeasurements` bails on a cache hit.
      for (const session of sessions) {
        if (!get().ivSessionCache[session.id]) {
          void get()
            .loadSessionMeasurements(session.id)
            .catch((err) => console.error('Failed to load IV measurements:', err));
        }
      }
    } catch (err) {
      console.error('Failed to fetch IV sessions:', err);
      set({ loading: false });
    }
  },

  selectSession: async (id: string | null) => {
    set({ selectedSessionId: id, measurements: [], findings: [], reports: [] });
    if (!id) return;
    set({ loading: true });
    try {
      const [measurements, findings, reports] = await Promise.all([
        ivApi.listMeasurements(id),
        ivApi.listFindings(id),
        ivApi.listReports(id),
      ]);
      set((state) => ({
        measurements,
        findings,
        reports,
        loading: false,
        ivSessionCache: { ...state.ivSessionCache, [id]: measurements },
      }));
    } catch (err) {
      console.error('Failed to load session data:', err);
      set({ loading: false });
    }
  },

  createSession: async (input: CreateSessionInput) => {
    const { currentSiteId } = get();
    if (!currentSiteId) throw new Error('Cannot create IV session: no site is selected');
    const session = await ivApi.createSession(input, currentSiteId);
    await get().fetchSessions();
    return session;
  },

  updateSession: async (id: string, input: UpdateSessionInput) => {
    await ivApi.updateSession(id, input);
    await get().fetchSessions();
  },

  deleteSession: async (id: string) => {
    await ivApi.deleteSession(id);
    const { selectedSessionId } = get();
    if (selectedSessionId === id) {
      set({ selectedSessionId: null, measurements: [], findings: [], reports: [] });
    }
    await get().fetchSessions();
  },

  importMeasurements: async (sessionId: string, payload: ImportPayload) => {
    const result = await ivApi.importMeasurements(sessionId, payload);
    await get().fetchMeasurements(sessionId);
    await get().fetchSessions();
    return result;
  },

  fetchMeasurements: async (sessionId: string) => {
    const measurements = await ivApi.listMeasurements(sessionId);
    set((state) => ({
      measurements,
      ivSessionCache: { ...state.ivSessionCache, [sessionId]: measurements },
    }));
  },

  loadSessionMeasurements: async (sessionId: string) => {
    if (get().ivSessionCache[sessionId]) return;
    const measurements = await ivApi.listMeasurements(sessionId);
    set((state) => ({
      ivSessionCache: { ...state.ivSessionCache, [sessionId]: measurements },
    }));
  },

  toggleMeasurementIncluded: async (measurementId: string, included: boolean) => {
    await ivApi.updateMeasurement(measurementId, { included: included ? 1 : 0 });
    const { selectedSessionId } = get();
    if (selectedSessionId) await get().fetchMeasurements(selectedSessionId);
  },

  updateMeasurement: async (id: string, input: UpdateMeasurementInput) => {
    await ivApi.updateMeasurement(id, input);
    const { selectedSessionId } = get();
    if (selectedSessionId) await get().fetchMeasurements(selectedSessionId);
  },

  bulkUpdateMeasurements: async (sessionId, ids, updates) => {
    set((state) => ({
      measurements: state.measurements.map((m) => {
        if (!ids.includes(m.id)) return m;
        const patch: Partial<typeof m> = {};
        if (updates.label     !== undefined) patch.label     = updates.label     ?? '';
        if (updates.marker2   !== undefined) patch.marker2   = updates.marker2;
        if (updates.marker3   !== undefined) patch.marker3   = updates.marker3;
        if (updates.map_label !== undefined) patch.map_label = updates.map_label;
        return { ...m, ...patch };
      }),
    }));
    await ivApi.bulkUpdateMeasurements(sessionId, ids, updates);
    await get().fetchMeasurements(sessionId);
  },

  bulkAssignMapLabels: async (sessionId, assignments) => {
    const assignMap = new Map(assignments.map(a => [a.id, a.map_label]));
    set((state) => ({
      measurements: state.measurements.map((m) => {
        if (!assignMap.has(m.id)) return m;
        return { ...m, map_label: assignMap.get(m.id) ?? null };
      }),
    }));
    await ivApi.bulkAssignMapLabels(sessionId, assignments);
    await get().fetchMeasurements(sessionId);
  },

  deleteMeasurement: async (id: string) => {
    await ivApi.deleteMeasurement(id);
    const { selectedSessionId } = get();
    if (selectedSessionId) await get().fetchMeasurements(selectedSessionId);
  },

  runAnalysis: async (sessionId: string) => {
    await ivApi.analyzeSession(sessionId);
    await get().fetchFindings(sessionId);
    await get().fetchSessions();
  },

  fetchFindings: async (sessionId: string) => {
    const findings = await ivApi.listFindings(sessionId);
    set({ findings });
  },

  generateReport: async (sessionId: string) => {
    await ivApi.generateReport(sessionId);
    await get().fetchReports(sessionId);
    await get().fetchSessions();
  },

  fetchReports: async (sessionId: string) => {
    const reports = await ivApi.listReports(sessionId);
    set({ reports });
  },
}));
